<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <v-sheet color="white" class="pa-8 mb-8 border-bottom shadow-sm">
      <v-row align="center">
        <v-col cols="12" md="7">
          <div class="d-flex align-center">
            <v-avatar size="56" color="primary lighten-5" class="mr-4 rounded-xl">
              <v-icon color="primary" large>fas fa-chart-line</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-black primary--text tracking-tighter mb-1">
                Información histórica
              </h1>
              <p class="body-2 grey--text font-weight-medium mb-0">
                {{ customerTitle }} · {{ periodLabel }}
              </p>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="5" class="d-flex justify-md-end align-center flex-wrap" style="gap: 8px">
          <v-btn
            outlined
            color="grey darken-2"
            class="rounded-xl px-5 text-none"
            @click="backToCustomer"
          >
            <v-icon left size="14">fas fa-arrow-left</v-icon>
            Volver al cliente
          </v-btn>
          <v-btn
            color="primary"
            class="rounded-xl px-5 font-weight-black text-none shadow-sm"
            :loading="loading"
            @click="load(true)"
          >
            <v-icon left size="14">fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-8">
      <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
        <v-row dense align="end">
          <v-col cols="12" sm="6" md="3">
            <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desde</div>
            <v-text-field v-model="startDate" outlined dense hide-details type="date" class="modern-input" @change="onCustomRange" />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Hasta</div>
            <v-text-field v-model="endDate" outlined dense hide-details type="date" class="modern-input" @change="onCustomRange" />
          </v-col>
          <v-col cols="12" md="6" class="d-flex align-center flex-wrap" style="gap: 8px">
            <v-btn
              small
              color="primary"
              class="rounded-xl text-none font-weight-bold"
              :outlined="rangePreset !== 'month'"
              @click="resetCurrentMonth"
            >Mes en curso</v-btn>
            <v-btn
              small
              color="primary"
              class="rounded-xl text-none font-weight-bold"
              :outlined="rangePreset !== 'year'"
              @click="resetCurrentYear"
            >Año en curso</v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-alert v-if="error" dense text type="error" class="mb-6">{{ error }}</v-alert>

      <v-row v-if="loading && !asOf" dense class="mb-8">
        <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="128" class="rounded-xl" />
        </v-col>
      </v-row>

      <template v-else>
        <v-row dense class="mb-8">
          <v-col cols="12" sm="6" md="3">
            <v-card class="bespoke-metric-card h-100" flat>
              <div class="metric-accent quoted-accent"></div>
              <v-card-text class="pa-6">
                <div class="metric-label">Cotizado</div>
                <div class="value">{{ kpis.quoted | moneda }}</div>
                <div class="status-msg mt-3">{{ kpis.count || 0 }} cotizaciones del período</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="bespoke-metric-card h-100" flat>
              <div class="metric-accent sold-accent"></div>
              <v-card-text class="pa-6">
                <div class="metric-label">Vendido</div>
                <div class="value">{{ kpis.confirmed | moneda }}</div>
                <div class="d-flex flex-column mt-3" style="gap: 2px">
                  <span class="status-msg">{{ kpis.confirmed_count || 0 }} OT confirmadas</span>
                  <span class="delta" :class="deltaClass(kpis.vs_previous_pct)">{{ deltaLabel(kpis.vs_previous_pct) }} vs período anterior</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="bespoke-metric-card h-100" flat>
              <div class="metric-accent billed-accent"></div>
              <v-card-text class="pa-6">
                <div class="metric-label">Facturado</div>
                <div class="value">{{ kpis.invoiced | moneda }}</div>
                <div class="d-flex flex-column mt-3" style="gap: 2px">
                  <span class="status-msg">{{ kpis.invoice_count || 0 }} facturas · neto NC</span>
                  <span class="delta" :class="deltaClass(kpis.vs_previous_invoiced_pct)">{{ deltaLabel(kpis.vs_previous_invoiced_pct) }} vs período anterior</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="bespoke-metric-card h-100" flat>
              <div class="metric-accent teal-accent"></div>
              <v-card-text class="pa-6">
                <div class="metric-label">Conversión</div>
                <div class="value">{{ kpis.conversion || 0 }}%</div>
                <div class="status-msg mt-3">vendido / cotizado · facturado {{ kpis.billed_ratio || 0 }}% de lo vendido</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row dense class="mb-8">
          <v-col cols="12" sm="6">
            <v-card class="bespoke-metric-card h-100" flat>
              <div class="metric-accent orange-accent"></div>
              <v-card-text class="pa-6">
                <div class="metric-label">Órdenes vigentes</div>
                <div class="value">{{ kpis.open_orders_count || 0 }}</div>
                <div class="status-msg mt-3">{{ kpis.open_orders_amount | moneda }} en OT abiertas</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card class="bespoke-metric-card h-100" flat>
              <div class="metric-accent purple-accent"></div>
              <v-card-text class="pa-6">
                <div class="metric-label">Cotizaciones abiertas</div>
                <div class="value">{{ kpis.open_quotes_count || 0 }}</div>
                <div class="status-msg mt-3">{{ kpis.open_quotes_amount | moneda }} sin OT ni rechazo</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card class="panel-card mb-8" flat>
          <div class="legend-row">
            <div>
              <div class="panel-title">Evolución en el tiempo</div>
              <div class="panel-sub">{{ chartSubtitle }}</div>
            </div>
            <div class="legend">
              <span><i class="swatch quoted"></i> Cotizado</span>
              <span><i class="swatch sold"></i> Vendido</span>
              <span><i class="swatch billed"></i> Facturado</span>
            </div>
          </div>
          <div v-if="!hasMovement" class="empty-mini py-10 text-center">Sin movimiento en el período</div>
          <div v-else style="height: 320px">
            <line-chart :key="chartKey" :chart-data="lineData" :options="lineOptions" :height="320" />
          </div>
        </v-card>

        <v-card class="panel-card mb-8" flat>
          <div class="panel-title">Órdenes vigentes</div>
          <div class="panel-sub mb-4">OT abiertas, independiente del período</div>
          <div v-if="!openOrders.length" class="empty-mini">Sin órdenes de trabajo abiertas</div>
          <v-simple-table v-else class="history-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>OT</th>
                  <th>Trabajo</th>
                  <th>Entrega</th>
                  <th class="text-right">Venta</th>
                  <th class="text-right">Facturado</th>
                  <th class="text-right">Pendiente</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in openOrders" :key="'ot-' + row.id" class="click-row" @click="openWorkOrder(row.id)">
                  <td class="font-weight-bold">#{{ row.id }}</td>
                  <td>
                    <div class="rank-name">{{ row.name || 'Sin nombre' }}</div>
                    <div v-if="row.products && row.products.length" class="rank-meta">{{ row.products.join(' · ') }}</div>
                  </td>
                  <td>{{ formatDay(row.delivery_date) }}</td>
                  <td class="text-right font-weight-bold">{{ row.total | moneda }}</td>
                  <td class="text-right">{{ row.invoiced | moneda }}</td>
                  <td class="text-right font-weight-bold" :class="{ 'is-loss': Number(row.pending || 0) > 0 }">{{ row.pending | moneda }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <v-row dense>
          <v-col cols="12" md="4">
            <v-card class="panel-card h-100" flat>
              <div class="panel-title">Cotizaciones abiertas</div>
              <div class="panel-sub mb-4">Pendientes o aprobadas, sin OT</div>
              <div v-if="!openQuotes.length" class="empty-mini">Sin cotizaciones abiertas</div>
              <div
                v-for="row in openQuotes"
                :key="'oq-' + row.id"
                class="mini-row"
                @click="openQuote(row.id)"
              >
                <div>
                  <div class="rank-name">{{ row.name || ('Cotización #' + row.id) }}</div>
                  <div class="rank-meta">#{{ row.id }} · {{ quoteStatus(row.status) }} · {{ formatDate(row.created_at) }}</div>
                </div>
                <div class="rank-val">{{ row.total | moneda }}</div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="panel-card h-100" flat>
              <div class="panel-title">Últimas facturas</div>
              <div class="panel-sub mb-4">Documentos más recientes del cliente</div>
              <div v-if="!latestInvoices.length" class="empty-mini">Sin documentos</div>
              <div
                v-for="row in latestInvoices"
                :key="'i-' + row.id"
                class="mini-row"
                @click="openInvoice(row.id)"
              >
                <div>
                  <div class="rank-name">{{ invoiceLabel(row) }}</div>
                  <div class="rank-meta">{{ formatDay(row.date_emited) }}<span v-if="row.work_order_id"> · OT #{{ row.work_order_id }}</span></div>
                </div>
                <div class="rank-val" :class="{ 'is-loss': Number(row.total || 0) < 0 }">{{ row.total | moneda }}</div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="panel-card h-100" flat>
              <div class="panel-title">Productos más vendidos</div>
              <div class="panel-sub mb-4">Venta confirmada del período</div>
              <div v-if="!products.length" class="empty-mini">Sin productos vendidos</div>
              <div v-for="row in products" :key="'p-' + (row.sku || row.name)" class="rank-row">
                <div class="rank-name">{{ row.name }}</div>
                <div class="rank-meta">{{ row.sku || 'Sin SKU' }} · {{ row.qty }} un.</div>
                <div class="rank-track">
                  <div class="rank-fill" :style="{ width: rankWidth(row.sales, maxProduct) }"></div>
                </div>
                <div class="rank-val">{{ row.sales | moneda }}</div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </div>
  </v-container>
</template>

<script>
import { fetchCustomerCommercialDashboard } from '@/api/reports'
import LineChart from '@/components/charts/LineChart.vue'
import notifications from '@/utils/notifications'
import moment from 'moment'
const numeral = require('numeral')

export default {
  name: 'CustomerHistory',
  components: { LineChart },
  data() {
    return {
      loading: true,
      requesting: false,
      error: '',
      rangePreset: 'year',
      startDate: moment().startOf('year').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      lastQuery: '',
      asOf: '',
      customer: {},
      lineData: { labels: [], datasets: [] },
      kpis: {
        quoted: 0,
        count: 0,
        confirmed: 0,
        confirmed_count: 0,
        conversion: 0,
        invoiced: 0,
        invoice_count: 0,
        billed_ratio: 0,
        vs_previous_pct: null,
        vs_previous_invoiced_pct: null,
        open_orders_count: 0,
        open_orders_amount: 0,
        open_quotes_count: 0,
        open_quotes_amount: 0
      },
      daily: [],
      openOrders: [],
      openQuotes: [],
      latestInvoices: [],
      products: []
    }
  },
  computed: {
    customerId() {
      return this.$route.params.customerId
    },
    customerTitle() {
      if (!this.customer || !this.customer.name) return 'Cliente'
      const rut = this.customer.rut ? this.customer.rut + ' · ' : ''
      return rut + (this.customer.fantasy || this.customer.name)
    },
    periodLabel() {
      const start = this.startDate ? moment(this.startDate).format('DD/MM/YYYY') : '—'
      const end = this.endDate ? moment(this.endDate).format('DD/MM/YYYY') : '—'
      return start + ' al ' + end
    },
    hasMovement() {
      return (this.chartRows || []).some((row) => Number(row.quoted || 0) || Number(row.confirmed || 0) || Number(row.invoiced || 0))
    },
    maxProduct() {
      let max = 1
      ;(this.products || []).forEach((row) => {
        const value = Number(row.sales || 0)
        if (value > max) max = value
      })
      return max
    },
    chartKey() {
      return String(this.customerId) + '-' + (this.startDate || '') + '-' + (this.endDate || '') + '-' + this.chartGrain + '-' + this.chartRows.length
    },
    chartGrain() {
      return this.rangePreset === 'year' ? 'month' : 'day'
    },
    chartSubtitle() {
      if (this.chartGrain === 'month') return 'Cotizado, vendido y facturado mes a mes'
      return 'Cotizado, vendido y facturado día a día'
    },
    chartRows() {
      if (this.chartGrain === 'month') return this.monthlyFromDaily(this.daily)
      return this.daily || []
    },
    lineOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#fff',
          titleFontColor: '#1A1F2E',
          bodyFontColor: '#607d8b',
          borderColor: '#eceff1',
          borderWidth: 1,
          cornerRadius: 8,
          xPadding: 12,
          yPadding: 12,
          callbacks: {
            label: (item, data) => `${data.datasets[item.datasetIndex].label}: $${numeral(item.yLabel).format('0,0')}`
          }
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { fontColor: '#9aa0a6', fontSize: 11, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }
          }],
          yAxes: [{
            gridLines: { color: '#f1f3f4', borderDash: [4, 4], drawBorder: false },
            ticks: {
              fontColor: '#9aa0a6',
              fontSize: 11,
              callback: (value) => '$' + numeral(value).format('0,0a')
            }
          }]
        }
      }
    }
  },
  watch: {
    customerId: {
      immediate: true,
      handler() {
        this.lastQuery = ''
        this.asOf = ''
        this.rangePreset = 'year'
        this.startDate = moment().startOf('year').format('YYYY-MM-DD')
        this.endDate = moment().format('YYYY-MM-DD')
        this.load(true)
      }
    }
  },
  methods: {
    backToCustomer() {
      this.$router.push({ name: 'CustomersCreateUpdate', params: { customerId: this.customerId } })
    },
    rankWidth(value, max) {
      return Math.max(8, Math.round((Number(value || 0) / Math.max(1, max)) * 100)) + '%'
    },
    monthlyFromDaily(days) {
      const buckets = {}
      const start = moment(this.startDate || moment().startOf('year')).startOf('month')
      const end = moment(this.endDate || undefined).startOf('month')
      const cursor = start.clone()
      while (cursor.isSameOrBefore(end, 'month')) {
        const key = cursor.format('YYYY-MM')
        buckets[key] = {
          date: key,
          label: cursor.format('MMM YYYY'),
          quoted: 0,
          confirmed: 0,
          invoiced: 0,
        }
        cursor.add(1, 'month')
      }
      ;(days || []).forEach((row) => {
        const key = moment(row.date).format('YYYY-MM')
        if (!buckets[key]) return
        buckets[key].quoted += Number(row.quoted || 0)
        buckets[key].confirmed += Number(row.confirmed || 0)
        buckets[key].invoiced += Number(row.invoiced || 0)
      })
      return Object.keys(buckets).sort().map((key) => buckets[key])
    },
    onCustomRange() {
      this.rangePreset = 'custom'
      this.load(true)
    },
    buildChart() {
      const rows = this.chartRows || []
      this.lineData = {
        labels: rows.map((row) => row.label),
        datasets: [
          {
            label: 'Cotizado',
            data: rows.map((row) => Number(row.quoted || 0)),
            borderColor: '#94a3b8',
            backgroundColor: 'transparent',
            borderWidth: 2,
            fill: false,
            lineTension: 0.35,
            pointRadius: 2,
            pointBackgroundColor: '#94a3b8'
          },
          {
            label: 'Vendido',
            data: rows.map((row) => Number(row.confirmed || 0)),
            borderColor: '#0f766e',
            backgroundColor: 'rgba(15, 118, 110, 0.06)',
            borderWidth: 3,
            fill: true,
            lineTension: 0.35,
            pointRadius: 2,
            pointBackgroundColor: '#0f766e'
          },
          {
            label: 'Facturado',
            data: rows.map((row) => Number(row.invoiced || 0)),
            borderColor: '#2563eb',
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [6, 4],
            fill: false,
            lineTension: 0.35,
            pointRadius: 2,
            pointBackgroundColor: '#2563eb'
          }
        ]
      }
    },
    deltaClass(value) {
      if (value == null) return ''
      if (value > 0) return 'is-up'
      if (value < 0) return 'is-down'
      return ''
    },
    deltaLabel(value) {
      if (value == null) return '—'
      const sign = value > 0 ? '+' : ''
      return sign + value + '%'
    },
    quoteStatus(status) {
      if (status === 'approved') return 'Aprobada'
      if (status === 'confirmed') return 'Confirmada'
      return 'Pendiente'
    },
    invoiceLabel(row) {
      const kind = Number(row.tipo) === 60 ? 'NC' : 'Factura'
      return row.folio ? kind + ' ' + row.folio : kind + ' #' + row.id
    },
    formatDate(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    formatDay(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    openQuote(id) {
      this.$router.push({ name: 'SalesNoteDetail', params: { sales_note: id } })
    },
    openInvoice(id) {
      this.$router.push({ name: 'CreateDTE', params: { id } })
    },
    openWorkOrder(id) {
      this.$router.push({ name: 'WorkOrderUpdate', params: { workOrder: id } })
    },
    async load(force) {
      if (!this.customerId) return
      const start = this.startDate || moment().startOf('year').format('YYYY-MM-DD')
      const end = this.endDate || moment().format('YYYY-MM-DD')
      const query = 'start=' + encodeURIComponent(start) + '&end=' + encodeURIComponent(end)
      if (this.requesting) return
      if (!force && this.lastQuery === query && this.asOf) return
      this.requesting = true
      this.loading = true
      this.error = ''
      try {
        const data = await fetchCustomerCommercialDashboard(this.customerId, query)
        if (!data || !data.as_of) throw new Error('Respuesta vacía')
        this.asOf = data.as_of
        this.lastQuery = query
        this.customer = data.customer || {}
        this.kpis = Object.assign({}, this.kpis, data.kpis || {})
        this.daily = data.daily || []
        this.openOrders = data.open_orders || []
        this.openQuotes = data.open_quotes || []
        this.latestInvoices = data.latest_invoices || data.recent_invoices || []
        this.products = data.products || []
        this.buildChart()
      } catch (err) {
        this.error = 'No se pudo cargar la información histórica del cliente.'
        notifications.showError('Error cargando histórico del cliente: ' + err)
      } finally {
        this.requesting = false
        this.loading = false
      }
    },
    resetCurrentMonth() {
      this.rangePreset = 'month'
      this.startDate = moment().startOf('month').format('YYYY-MM-DD')
      this.endDate = moment().format('YYYY-MM-DD')
      this.load(true)
    },
    resetCurrentYear() {
      this.rangePreset = 'year'
      this.startDate = moment().startOf('year').format('YYYY-MM-DD')
      this.endDate = moment().format('YYYY-MM-DD')
      this.load(true)
    }
  }
}
</script>

<style scoped>
.dashboard-container { background-color: #f8fafc; min-height: 100vh; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08) !important; }
.shadow-sm { box-shadow: 0 4px 15px rgba(0,0,0,0.03) !important; }
.rounded-xl { border-radius: 12px !important; }
.tracking-tighter { letter-spacing: -1.5px !important; }
.tracking-widest { letter-spacing: 1.8px; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.bespoke-metric-card {
  position: relative;
  background: white !important;
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 16px !important;
  overflow: hidden;
}
.metric-accent { position: absolute; top: 0; left: 0; right: 0; height: 4px; }
.quoted-accent { background: #94a3b8; }
.sold-accent { background: #0f766e; }
.billed-accent { background: #2563eb; }
.teal-accent { background: #0f766e; }
.orange-accent { background: #ff9800; }
.purple-accent { background: #7c3aed; }
.metric-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
}
.value { font-size: 1.35rem; font-weight: 800; color: #1e293b; letter-spacing: -1px; }
.status-msg { font-size: 0.7rem; font-weight: 600; color: #94a3b8; }
.delta { font-size: 0.7rem; font-weight: 800; color: #94a3b8; }
.delta.is-up { color: #15803d; }
.delta.is-down { color: #be123c; }
.panel-card {
  background: #fff !important;
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 16px !important;
  padding: 20px 22px;
}
.legend-row { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; margin-bottom: 16px; }
.legend { display: flex; gap: 16px; font-size: 12px; font-weight: 700; color: #64748b; flex-wrap: wrap; }
.swatch { display: inline-block; width: 10px; height: 10px; border-radius: 999px; margin-right: 6px; }
.swatch.quoted { background: #94a3b8; }
.swatch.sold { background: #0f766e; }
.swatch.billed { background: #2563eb; }
.panel-title { font-size: 0.95rem; font-weight: 800; color: #1e293b; }
.panel-sub { font-size: 12px; font-weight: 600; color: #64748b; margin-top: 2px; }
.rank-row { display: grid; grid-template-columns: 1fr 110px; grid-template-rows: auto auto auto; gap: 2px 12px; margin-bottom: 12px; }
.rank-name { font-size: 13px; font-weight: 700; color: #1e293b; }
.rank-meta { font-size: 11px; font-weight: 600; color: #64748b; }
.rank-track { grid-column: 1 / -1; height: 6px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.rank-fill { height: 100%; background: #0f766e; }
.rank-val { font-size: 12px; font-weight: 800; color: #1e293b; grid-column: 2; grid-row: 1 / 3; align-self: center; text-align: right; }
.rank-val.is-loss, .is-loss { color: #be123c; }
.mini-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0,0,0,0.04);
  cursor: pointer;
}
.mini-row:hover .rank-name, .click-row:hover .rank-name { color: #0f766e; }
.click-row { cursor: pointer; }
.empty-mini { font-size: 13px; font-weight: 600; color: #94a3b8; }
.history-table th { font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; }
.history-table td { font-size: 13px; color: #1e293b; }
</style>
